/**
 * extractSubqueries — token-aware finder for top-level `(SELECT … )`,
 * `(WITH … )` and `(VALUES … )` paren groups inside a clause body. Used by
 * FROM / JOIN RHS / WHERE / HAVING / SELECT-list rendering to plug in nested
 * `<SubquerySection>` components.
 *
 * "Top-level" here means at paren-depth 0 _within the clause body_ — a paren
 * group nested inside another paren is NOT picked up; the outer extractor
 * yields the outer group, and the SubquerySection's inner `<SelectRenderer>`
 * will recursively re-extract from the body when expanded. Pure / structural
 * (VQE-RO9 — no regex parsing of SQL).
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { consumeParenGroup } from '../insert/insertParseHelpers';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

export type SubqueryBodyKind = 'select' | 'values' | 'unknown';
export type SubqueryPrefix = 'IN' | 'LATERAL' | null;

export interface SubqueryRef {
  /** Index of the `(`. */
  readonly parenStart: number;
  /** Index AFTER the matching `)`. */
  readonly parenEndExclusive: number;
  /** First non-whitespace inside the parens. */
  readonly bodyStart: number;
  /** Last non-whitespace + 1 inside the parens. */
  readonly bodyEnd: number;
  /** First-keyword classification of the body. */
  readonly bodyKind: SubqueryBodyKind;
  /** Immediately-preceding keyword token (case-insensitive), if any. */
  readonly precededBy: SubqueryPrefix;
  /** End-exclusive index of the preceding keyword (used to split text around it). */
  readonly prefixStart: number | null;
  readonly prefixEnd: number | null;
}

export function extractSubqueries(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): SubqueryRef[] {
  const ctx = skipContextFor(options);
  const out: SubqueryRef[] = [];
  let i = start;
  let lastWord: { upper: string; start: number; end: number } | null = null;

  while (i < endExclusive) {
    const skipped = trySkipNonCode(source, i, endExclusive, ctx);
    if (skipped >= 0) {
      // Strings / comments / quoted identifiers don't qualify as a "preceding
      // keyword" for IN / LATERAL, so clear the lastWord tracker.
      lastWord = null;
      i = skipped;
      continue;
    }
    const c = source[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '(') {
      // Capture the immediately-preceding keyword (if it ended right before
      // optional whitespace before this paren).
      const precededBy = lastWord && (lastWord.upper === 'IN' || lastWord.upper === 'LATERAL')
        ? lastWord
        : null;
      const groupEnd = consumeParenGroup(source, i, endExclusive, ctx);
      const innerStart = i + 1;
      const innerEnd = groupEnd > 0 ? groupEnd - 1 : endExclusive;
      const bodyStart = trimLeading(source, innerStart, innerEnd);
      const bodyEnd = trimTrailing(source, bodyStart, innerEnd);
      const kind = detectSubqueryBodyKind(source, bodyStart, bodyEnd);
      if (kind !== 'unknown') {
        out.push({
          parenStart: i,
          parenEndExclusive: groupEnd,
          bodyStart,
          bodyEnd,
          bodyKind: kind,
          precededBy: precededBy ? (precededBy.upper as SubqueryPrefix) : null,
          prefixStart: precededBy ? precededBy.start : null,
          prefixEnd: precededBy ? precededBy.end : null,
        });
      }
      i = groupEnd;
      lastWord = null;
      continue;
    }
    if (c === ')') { i++; lastWord = null; continue; }
    if (IDENT_START.test(c)) {
      const wStart = i;
      i++;
      while (i < endExclusive && IDENT_CONT.test(source[i])) i++;
      lastWord = { upper: source.slice(wStart, i).toUpperCase(), start: wStart, end: i };
      continue;
    }
    // Operator characters / punctuation — break the keyword adjacency unless
    // they're trivially-adjacent (a `.` or `,`). Conservatively clear.
    lastWord = null;
    i++;
  }
  return out;
}

/**
 * Scan a subquery body for `<alias>.` references where `alias` is in the
 * supplied set of outer-scope aliases. Returns the matched aliases.
 *
 * Token-aware so quoted identifiers / strings / comments don't trigger false
 * positives (e.g. `'a.x'` inside a string literal).
 */
export function findCorrelatedAliases(
  source: string,
  bodyStart: number,
  bodyEnd: number,
  outerAliases: readonly string[],
  options: ParseOptions,
): string[] {
  if (outerAliases.length === 0) return [];
  const ctx = skipContextFor(options);
  const lowerSet = new Set(outerAliases.map((a) => a.toLowerCase()));
  const found = new Set<string>();
  let i = bodyStart;
  while (i < bodyEnd) {
    const skipped = trySkipNonCode(source, i, bodyEnd, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (!IDENT_START.test(c)) { i++; continue; }
    const wStart = i;
    i++;
    while (i < bodyEnd && IDENT_CONT.test(source[i])) i++;
    if (i < bodyEnd && source[i] === '.') {
      const word = source.slice(wStart, i);
      const lower = word.toLowerCase();
      if (lowerSet.has(lower)) {
        // Resolve back to the originally-cased alias for the tooltip copy.
        const original = outerAliases.find((a) => a.toLowerCase() === lower);
        if (original) found.add(original);
      }
    }
  }
  return Array.from(found);
}

function trimLeading(source: string, start: number, end: number): number {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  return i;
}

function trimTrailing(source: string, start: number, end: number): number {
  let i = end;
  while (i > start && /\s/.test(source[i - 1])) i--;
  return i;
}

export function detectSubqueryBodyKind(source: string, start: number, end: number): SubqueryBodyKind {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (i >= end || !IDENT_START.test(source[i])) return 'unknown';
  const wStart = i;
  i++;
  while (i < end && IDENT_CONT.test(source[i])) i++;
  const upper = source.slice(wStart, i).toUpperCase();
  if (upper === 'SELECT' || upper === 'WITH') return 'select';
  if (upper === 'VALUES') return 'values';
  return 'unknown';
}
